import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import ReactDOM from 'react-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import './DemandPlannerGraphComponent.scss';
import {
  DAYS,
  DAY_LABELS,
  formatHourShort,
  formatHourFull,
  getHoursOfOperation,
  isOffHours,
} from './demand-planner-helpers';
import BulkEditToolbarComponent from './BulkEditToolbarComponent';
import GenerateWeeklyDemandModal from './GenerateWeeklyDemandModal';

const MAX_HEADCOUNT = 99;

// Self-contained edit popover — value state lives here so stepper clicks
// only re-render this small component, not the entire chart.
const EditPopover = React.memo(
  ({
    hour,
    initialValue,
    x,
    y,
    roleName,
    roleColor,
    dayLabel,
    chartContainerLeft,
    chartContainerTop,
    chartContainerWidth,
    onCommit,
    onDiscard,
  }) => {
    const [value, setValue] = useState(initialValue);
    const popoverReference = useRef(null);

    // Clamp value within bounds
    const handleChange = useCallback((newValue) => {
      setValue(Math.max(0, Math.min(MAX_HEADCOUNT, newValue)));
    }, []);

    // Close on outside click or Escape
    useEffect(() => {
      const handleClickOutside = (event_) => {
        if (
          popoverReference.current &&
          !popoverReference.current.contains(event_.target)
        ) {
          onDiscard();
        }
      };

      const handleEscape = (event_) => {
        if (event_.key === 'Escape') onDiscard();
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }, [onDiscard]);

    // Convert chart-relative coords to viewport coords for fixed positioning.
    // The portal renders at document.body, outside the chart DOM subtree, so
    // browser extensions (1Password, MetaMask) scanning from the <input> won't
    // traverse the 300+ element SVG chart — eliminating ~1 s hover lag.
    const popoverWidth = 200;
    const popoverHeight = 180;
    const viewportX = chartContainerLeft + x;
    const viewportY = chartContainerTop + y;
    let left = viewportX - popoverWidth / 2;
    let top = viewportY - popoverHeight - 12;
    if (left < 8) left = 8;
    if (left + popoverWidth > chartContainerLeft + chartContainerWidth - 8)
      left = chartContainerLeft + chartContainerWidth - popoverWidth - 8;
    if (top < 8) top = viewportY + 16;

    return ReactDOM.createPortal(
      <div
        ref={popoverReference}
        className="edit-popover"
        data-1p-ignore
        style={{ position: 'fixed', left, top }}
      >
        <div className="popover-header">
          <span
            className="popover-color"
            style={{ backgroundColor: roleColor }}
          />
          <span className="popover-title">{roleName}</span>
          <button
            className="popover-close"
            title="Discard changes"
            type="button"
            onClick={onDiscard}
          >
            &times;
          </button>
        </div>
        <div className="popover-time">
          {dayLabel} at {formatHourFull(hour)}
        </div>
        <div className="popover-controls">
          <button
            className="stepper-btn"
            disabled={value <= 0}
            type="button"
            onClick={() => handleChange(value - 1)}
          >
            &minus;
          </button>
          <input
            autoComplete="off"
            autoFocus
            className="headcount-input"
            data-1p-ignore
            data-form-type="other"
            data-lpignore="true"
            max={MAX_HEADCOUNT}
            min={0}
            type="number"
            value={value}
            onChange={(event_) => {
              const v = Number.parseInt(event_.target.value, 10);
              if (!Number.isNaN(v)) handleChange(v);
            }}
            onKeyDown={(event_) => {
              if (event_.key === 'Enter') {
                onCommit(value);
              } else if (event_.key === 'ArrowUp') {
                event_.preventDefault();
                handleChange(value + 1);
              } else if (event_.key === 'ArrowDown') {
                event_.preventDefault();
                handleChange(value - 1);
              }
            }}
          />
          <button
            className="stepper-btn"
            disabled={value >= MAX_HEADCOUNT}
            type="button"
            onClick={() => handleChange(value + 1)}
          >
            +
          </button>
        </div>
        <button
          className="popover-ok"
          type="button"
          onClick={() => onCommit(value)}
        >
          OK
        </button>
      </div>,
      document.body
    );
  }
);

// Custom shape for the empty-column hint bar
const EmptyColumnHint = React.memo(({ x, y, width, height, payload }) => {
  if (!payload || payload.total !== 0 || height <= 0) return null;

  const isOff = payload.isOffHours;

  return (
    <g className={`empty-column-hint${isOff ? ' off-hours' : ''}`}>
      <rect
        className="hint-bg"
        cursor="pointer"
        fill="transparent"
        height={height}
        rx={2}
        width={width}
        x={x}
        y={y}
      />
      <rect
        className="hint-border"
        fill="none"
        height={height - 2}
        rx={2}
        stroke="#ccc"
        strokeDasharray="4 3"
        strokeWidth={1}
        width={width - 2}
        x={x + 1}
        y={y + 1}
      />
      <text
        className="hint-plus"
        cursor="pointer"
        dominantBaseline="middle"
        fill="#bbb"
        fontSize={16}
        fontWeight={300}
        textAnchor="middle"
        x={x + width / 2}
        y={y + height / 2}
      >
        +
      </text>
    </g>
  );
});

const ROLE_COLORS_FALLBACK = [
  '#0a84ff',
  '#26ce93',
  '#FF4650',
  '#f5a623',
  '#5AC8FA',
  '#AF52DE',
  '#FF6B35',
  '#FF2D55',
  '#34C759',
  '#6D7278',
];

// Stable recharts configuration objects — defined at module level so they
// maintain referential identity across renders, preventing recharts from
// doing unnecessary internal reconciliation of axes, grid, and tooltip.
const CHART_MARGIN = { top: 28, right: 16, left: 8, bottom: 4 };
const AXIS_LINE = { stroke: '#d1d1d1' };
const AXIS_TICK = { fontSize: 12, fill: '#6D7278' };
const YAXIS_LABEL_CONFIG = {
  value: 'Headcount',
  angle: -90,
  position: 'insideLeft',
  offset: 4,
  style: { fontSize: 12, fill: '#6D7278' },
};
const TOOLTIP_CURSOR = { fill: 'rgba(10, 132, 255, 0.05)' };

const DemandPlannerGraphComponent = ({
  roles = [],
  enabledRoles = [],
  onDemandDataUpdate,
  apiDemandData = {},
  originalApiDemandData = {},
  demandDataLoading = false,
  demandDataError = null,
  onBulkCopyDay,
  onBulkFillRange,
  onBulkUndo,
  canBulkUndo = false,
  locationId,
  locationCategory,
  usersByRole,
  onGenerateSuccess,
}) => {
  const [activeDay, setActiveDay] = useState(0);
  const [selection, setSelection] = useState(null);
  const [focusedCell, setFocusedCell] = useState(null);
  const [lastCommit, setLastCommit] = useState(null);
  const chartContainerReference = useRef(null);
  const dayTabsReference = useRef(null);
  const previousSelectionReference = useRef(null);
  const barClickedReference = useRef(false);
  const rolePickerReference = useRef(null);
  const [rolePicker, setRolePicker] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  // O(1) role lookup map — replaces roles.find() calls throughout the component
  const roleMap = useMemo(
    () => Object.fromEntries(roles.map((r) => [r.id, r])),
    [roles]
  );

  const getRoleColor = useCallback(
    (roleId, roleIndex) => {
      const role = roleMap[roleId];
      if (role) {
        const color = role.color_code || role.color || role.tag_color;
        if (color) return color.startsWith('#') ? color : `#${color}`;
      }

      return ROLE_COLORS_FALLBACK[roleIndex % ROLE_COLORS_FALLBACK.length];
    },
    [roleMap]
  );

  const roleColorMap = useMemo(() => {
    const map = {};
    enabledRoles.forEach((roleId, index) => {
      map[roleId] = getRoleColor(roleId, index);
    });

    return map;
  }, [enabledRoles, getRoleColor]);

  // Load hours-of-operation config from sessionStorage
  const hoursConfig = useMemo(() => getHoursOfOperation(), []);

  // Reset keyboard focus when day changes
  useEffect(() => {
    setFocusedCell(null);
  }, [activeDay]);

  // Transform demand data into recharts-compatible array for the active day
  const chartData = useMemo(() => {
    const day = DAYS[activeDay];
    const data = [];
    for (let hour = 0; hour < 24; hour++) {
      const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
      const entry = {
        hour,
        hourLabel: formatHourShort(hour),
        timeSlot,
        isOffHours: isOffHours(hour, activeDay, hoursConfig),
      };
      let total = 0;
      enabledRoles.forEach((roleId) => {
        const value = apiDemandData[roleId]?.[day]?.[timeSlot] || 0;
        entry[`role_${roleId}`] = value;
        total += value;
      });

      entry.total = total;
      data.push(entry);
    }

    return data;
  }, [activeDay, enabledRoles, apiDemandData, hoursConfig]);

  // Auto-scale Y axis with 20% headroom
  const maxY = useMemo(() => {
    const maxTotal = Math.max(...chartData.map((d) => d.total), 1);

    return Math.ceil(maxTotal * 1.2);
  }, [chartData]);

  // Stable domain array for YAxis — prevents recharts from re-computing axis layout
  const yDomain = useMemo(() => [0, maxY], [maxY]);

  // Add empty-column hint values for the background hint bar
  const chartDataWithHints = useMemo(
    () =>
      chartData.map((entry) => ({
        ...entry,
        _emptyHint: entry.total === 0 ? maxY : 0,
      })),
    [chartData, maxY]
  );

  // Handle clicking a bar segment — open the edit popover
  const handleBarClick = useCallback(
    (roleId) => (data, index, event) => {
      if (!event) return;
      barClickedReference.current = true;
      setRolePicker(null);
      const rect = chartContainerReference.current?.getBoundingClientRect();
      const day = DAYS[activeDay];
      const { timeSlot } = data;
      const currentValue = apiDemandData[roleId]?.[day]?.[timeSlot] || 0;

      const clickedRoleIndex = enabledRoles.indexOf(roleId);

      setFocusedCell({ hour: data.hour, roleIndex: clickedRoleIndex });
      setSelection({
        roleId,
        day,
        timeSlot,
        hour: data.hour,
        value: currentValue,
        x: event.clientX - (rect?.left || 0),
        y: event.clientY - (rect?.top || 0),
      });
    },
    [activeDay, apiDemandData, enabledRoles]
  );

  // Pre-compute click handlers per role to avoid creating new functions on each render
  const barClickHandlers = useMemo(() => {
    const handlers = {};
    enabledRoles.forEach((roleId) => {
      handlers[roleId] = handleBarClick(roleId);
    });

    return handlers;
  }, [enabledRoles, handleBarClick]);

  // Handle clicking on the chart background (empty areas)
  const handleChartClick = useCallback(
    (chartState, event) => {
      // Skip if a bar segment was clicked (handled by handleBarClick)
      if (barClickedReference.current) {
        barClickedReference.current = false;
        setRolePicker(null);
        return;
      }

      if (
        !chartState ||
        chartState.activeTooltipIndex === null ||
        chartState.activeTooltipIndex === undefined
      )
        return;

      const hourIndex = chartState.activeTooltipIndex;
      const day = DAYS[activeDay];
      const timeSlot = `${hourIndex.toString().padStart(2, '0')}:00`;

      const rect = chartContainerReference.current?.getBoundingClientRect();
      const clickX = event?.clientX ? event.clientX - (rect?.left || 0) : 0;
      const clickY = event?.clientY ? event.clientY - (rect?.top || 0) : 0;

      // Close any existing selection
      setSelection(null);

      if (enabledRoles.length === 1) {
        // Single role — open edit popover directly
        const roleId = enabledRoles[0];
        const currentValue = apiDemandData[roleId]?.[day]?.[timeSlot] || 0;
        setSelection({
          roleId,
          day,
          timeSlot,
          hour: hourIndex,
          value: currentValue,
          x: clickX,
          y: clickY,
        });
      } else if (enabledRoles.length > 1) {
        // Multiple roles — show role picker
        setRolePicker({
          hour: hourIndex,
          timeSlot,
          day,
          x: clickX,
          y: clickY,
        });
      }
    },
    [activeDay, apiDemandData, enabledRoles]
  );

  // Handle selecting a role from the role picker
  const handleRolePickerSelect = useCallback(
    (roleId) => {
      if (!rolePicker) return;
      const { hour, timeSlot, day, x, y } = rolePicker;
      const currentValue = apiDemandData[roleId]?.[day]?.[timeSlot] || 0;

      setRolePicker(null);
      setSelection({
        roleId,
        day,
        timeSlot,
        hour,
        value: currentValue,
        x,
        y,
      });
    },
    [rolePicker, apiDemandData]
  );

  // Handle keyboard navigation within the role picker
  const handleRolePickerKeyDown = useCallback((event_) => {
    const items = rolePickerReference.current?.querySelectorAll(
      '[role="menuitem"]'
    );
    if (!items || items.length === 0) return;

    const currentIndex = [...items].indexOf(document.activeElement);

    switch (event_.key) {
      case 'ArrowDown':
        event_.preventDefault();
        items[(currentIndex + 1) % items.length].focus();
        break;
      case 'ArrowUp':
        event_.preventDefault();
        items[(currentIndex - 1 + items.length) % items.length].focus();
        break;
      case 'Tab':
        // Trap focus within the picker
        event_.preventDefault();
        if (event_.shiftKey) {
          items[(currentIndex - 1 + items.length) % items.length].focus();
        } else {
          items[(currentIndex + 1) % items.length].focus();
        }

        break;
      case 'Escape':
        event_.preventDefault();
        setRolePicker(null);
        break;
      default:
        break;
    }
  }, []);

  // Auto-dismiss the undo bar after 5 seconds
  useEffect(() => {
    if (!lastCommit) return;
    const timer = setTimeout(() => setLastCommit(null), 5000);

    return () => clearTimeout(timer);
  }, [lastCommit]);

  // OK — commit the value to the parent and close the popover
  const handleCommit = useCallback(
    (value) => {
      if (!selection || !onDemandDataUpdate) return;
      const { roleId, day, timeSlot } = selection;
      const currentValue =
        apiDemandData[roleId]?.[day]?.[timeSlot] || 0;

      if (value !== currentValue) {
        onDemandDataUpdate({
          [roleId]: { [day]: { [timeSlot]: value } },
        });
        setLastCommit({ roleId, day, timeSlot, previousValue: currentValue });
      }

      setSelection(null);
    },
    [selection, onDemandDataUpdate, apiDemandData]
  );

  // X / Escape / outside click — discard and close
  const handleDiscard = useCallback(() => {
    setSelection(null);
  }, []);

  // Undo the last committed cell change (shown outside the popover)
  const handleUndoLastCommit = useCallback(() => {
    if (!lastCommit || !onDemandDataUpdate) return;
    const { roleId, day, timeSlot, previousValue } = lastCommit;
    onDemandDataUpdate({
      [roleId]: { [day]: { [timeSlot]: previousValue } },
    });
    setLastCommit(null);
  }, [lastCommit, onDemandDataUpdate]);

  // Close role picker on outside click or Escape (popover handles its own)
  useEffect(() => {
    if (!rolePicker) return;

    const handleClickOutside = (event_) => {
      if (
        rolePickerReference.current &&
        !rolePickerReference.current.contains(event_.target)
      ) {
        setRolePicker(null);
      }
    };

    const handleEscape = (event_) => {
      if (event_.key === 'Escape') setRolePicker(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [rolePicker]);

  // Focus first item when role picker opens
  useEffect(() => {
    if (rolePicker && rolePickerReference.current) {
      const firstItem = rolePickerReference.current.querySelector(
        '[role="menuitem"]'
      );
      if (firstItem) firstItem.focus();
    }
  }, [rolePicker]);

  // Return focus to chart container when popover closes
  useEffect(() => {
    if (previousSelectionReference.current && !selection) {
      if (chartContainerReference.current) {
        chartContainerReference.current.focus();
      }
    }

    previousSelectionReference.current = selection;
  }, [selection]);

  // Compute description for the focused cell (aria-live announcements)
  const focusedCellDescription = useMemo(() => {
    if (!focusedCell || enabledRoles.length === 0) return '';
    const { hour, roleIndex } = focusedCell;
    const roleId = enabledRoles[roleIndex];
    const role = roleMap[roleId];
    const roleName = role?.name || `Role ${roleId}`;
    const day = DAYS[activeDay];
    const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
    const value = apiDemandData[roleId]?.[day]?.[timeSlot] || 0;

    return `${roleName}, ${formatHourFull(hour)}, ${value} staff`;
  }, [focusedCell, enabledRoles, roleMap, activeDay, apiDemandData]);

  // Handle keyboard navigation on the chart
  const handleChartKeyDown = useCallback(
    (event_) => {
      // Don't handle keyboard nav while popover or role picker is open
      if (selection) return;
      if (rolePicker) return;
      if (enabledRoles.length === 0) return;

      // Initialize focus if not set
      if (!focusedCell) {
        if (
          ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter'].includes(
            event_.key
          )
        ) {
          event_.preventDefault();
          setFocusedCell({ hour: 0, roleIndex: 0 });
        }

        return;
      }

      const { hour, roleIndex } = focusedCell;

      switch (event_.key) {
        case 'ArrowLeft':
          event_.preventDefault();
          setFocusedCell({
            hour: hour > 0 ? hour - 1 : 23,
            roleIndex,
          });
          break;
        case 'ArrowRight':
          event_.preventDefault();
          setFocusedCell({
            hour: hour < 23 ? hour + 1 : 0,
            roleIndex,
          });
          break;
        case 'ArrowUp':
          event_.preventDefault();
          setFocusedCell({
            hour,
            roleIndex: roleIndex > 0 ? roleIndex - 1 : enabledRoles.length - 1,
          });
          break;
        case 'ArrowDown':
          event_.preventDefault();
          setFocusedCell({
            hour,
            roleIndex: roleIndex < enabledRoles.length - 1 ? roleIndex + 1 : 0,
          });
          break;
        case 'Enter': {
          event_.preventDefault();
          const roleId = enabledRoles[roleIndex];
          const day = DAYS[activeDay];
          const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
          const currentValue = apiDemandData[roleId]?.[day]?.[timeSlot] || 0;

          // Approximate bar position for popover placement
          const container = chartContainerReference.current;
          const containerWidth = container?.offsetWidth || 800;
          const plotLeft = 52;
          const plotWidth = containerWidth - plotLeft - 16;
          const x = plotLeft + ((hour + 0.5) / 24) * plotWidth;

          setSelection({
            roleId,
            day,
            timeSlot,
            hour,
            value: currentValue,
            x,
            y: 120,
          });
          break;
        }

        default:
          break;
      }
    },
    [selection, rolePicker, focusedCell, enabledRoles, activeDay, apiDemandData]
  );

  // Single stable handler for day tab clicks using data-index delegation
  const handleDayClick = useCallback((event_) => {
    const index = Number(event_.currentTarget.dataset.index);
    if (Number.isNaN(index)) return;
    setActiveDay(index);
    setSelection(null);
    setRolePicker(null);
  }, []);

  // Handle keyboard navigation on day tabs (Left/Right arrow keys)
  const handleDayTabKeyDown = useCallback(
    (event_) => {
      if (event_.key !== 'ArrowLeft' && event_.key !== 'ArrowRight') {
        return;
      }

      event_.preventDefault();
      const newIndex =
        event_.key === 'ArrowLeft'
          ? (activeDay - 1 + 7) % 7
          : (activeDay + 1) % 7;

      setActiveDay(newIndex);
      setSelection(null);
      setRolePicker(null);

      const tabs = dayTabsReference.current
        ? dayTabsReference.current.querySelectorAll('.day-tab')
        : [];

      if (tabs[newIndex]) {
        tabs[newIndex].focus();
      }
    },
    [activeDay]
  );

  // Initialize focus cell when chart container receives focus
  const handleChartFocus = useCallback(() => {
    if (!focusedCell && enabledRoles.length > 0) {
      setFocusedCell({ hour: 0, roleIndex: 0 });
    }
  }, [focusedCell, enabledRoles.length]);

  // Clear virtual focus when chart loses focus entirely
  const handleChartBlur = useCallback((event_) => {
    if (!chartContainerReference.current?.contains(event_.relatedTarget)) {
      setFocusedCell(null);
    }
  }, []);

  // Custom hover tooltip
  const renderCustomTooltip = useCallback(
    ({ active, payload }) => {
      if (!active || !payload || payload.length === 0) return null;
      const data = payload[0]?.payload;
      if (!data) return null;

      return (
        <div className="demand-chart-tooltip">
          <div className="tooltip-header">
            {DAYS[activeDay]} at {formatHourFull(data.hour)}
          </div>
          <div className="tooltip-rows">
            {enabledRoles.map((roleId) => {
              const role = roleMap[roleId];
              const value = data[`role_${roleId}`] || 0;

              return (
                <div key={roleId} className="tooltip-row">
                  <span
                    className="tooltip-color"
                    style={{ backgroundColor: roleColorMap[roleId] }}
                  />
                  <span className="tooltip-role">
                    {role?.name || `Role ${roleId}`}
                  </span>
                  <span className="tooltip-value">{value}</span>
                </div>
              );
            })}
          </div>
          <div className="tooltip-total">
            Total: <strong>{data.total}</strong>
          </div>
        </div>
      );
    },
    [activeDay, enabledRoles, roleMap, roleColorMap]
  );

  // Total label rendered on top of the highest stacked bar segment
  const renderTotalLabel = useCallback(
    (properties) => {
      const { x, y, width, index } = properties;
      const total = chartData[index]?.total;
      if (!total || total === 0) return null;

      return (
        <text
          fill="#555"
          fontSize={11}
          fontWeight={600}
          textAnchor="middle"
          x={x + width / 2}
          y={y - 8}
        >
          {total}
        </text>
      );
    },
    [chartData]
  );

  // Position the role picker near the click point
  const rolePickerStyle = useMemo(() => {
    if (!rolePicker || !chartContainerReference.current) return {};
    const containerWidth = chartContainerReference.current.offsetWidth;
    const pickerWidth = 180;

    let left = rolePicker.x - pickerWidth / 2;
    const top = rolePicker.y + 8;

    // Clamp horizontal
    if (left < 8) left = 8;
    if (left + pickerWidth > containerWidth - 8)
      left = containerWidth - pickerWidth - 8;

    return { left, top };
  }, [rolePicker]);

  // --- Render ---

  if (demandDataLoading) {
    return (
      <div className="demand-planner-graph">
        <div className="graph-status loading">Loading demand data...</div>
      </div>
    );
  }

  if (demandDataError) {
    return (
      <div className="demand-planner-graph">
        <div className="graph-status error">
          Error loading demand data: {demandDataError.message}
        </div>
      </div>
    );
  }

  const lastRoleId =
    enabledRoles.length > 0 ? enabledRoles[enabledRoles.length - 1] : null;

  // Pre-compute popover props to avoid jsx-curly-newline lint conflicts
  const containerRect =
    chartContainerReference.current?.getBoundingClientRect();
  const popoverContainerWidth = containerRect?.width || 800;
  const popoverContainerLeft = containerRect?.left || 0;
  const popoverContainerTop = containerRect?.top || 0;
  const popoverRoleName = selection
    ? roleMap[selection.roleId]?.name || `Role ${selection.roleId}`
    : '';

  return (
    <div className="demand-planner-graph">
      <div className="graph-header">
        <h3>Weekly Hourly Demand Forecast</h3>
        <p className="graph-subtitle">
          Click any bar to edit, or click empty areas to add demand
        </p>
      </div>

      {/* Day tabs */}
      <div ref={dayTabsReference} className="day-tabs" role="tablist">
        <div className="day-tabs-list">
          {DAYS.map((day, index) => (
            <button
              key={day}
              aria-selected={index === activeDay}
              className={`day-tab ${index === activeDay ? 'active' : ''}`}
              data-index={index}
              role="tab"
              tabIndex={index === activeDay ? 0 : -1}
              type="button"
              onClick={handleDayClick}
              onKeyDown={handleDayTabKeyDown}
            >
              {DAY_LABELS[index]}
            </button>
          ))}
        </div>
        <button
          className="generate-demand-btn"
          type="button"
          onClick={() => setShowGenerateModal(true)}
        >
          Auto-Generate Weekly Demand
          <span className="beta-tag">Beta</span>
        </button>
      </div>

      {/* Bulk edit toolbar */}
      <BulkEditToolbarComponent
        activeDay={activeDay}
        canBulkUndo={canBulkUndo}
        enabledRoles={enabledRoles}
        roles={roles}
        usersByRole={usersByRole}
        onBulkCopyDay={onBulkCopyDay}
        onBulkFillRange={onBulkFillRange}
        onBulkUndo={onBulkUndo}
      />

      {/* Role legend */}
      {enabledRoles.length > 0 && (
        <div className="role-legend">
          {enabledRoles.map((roleId) => {
            const role = roleMap[roleId];

            return (
              <div key={roleId} className="legend-item">
                <span
                  className="legend-color"
                  style={{ backgroundColor: roleColorMap[roleId] }}
                />
                <span className="legend-label">
                  {role?.name || `Role ${roleId}`}
                </span>
              </div>
            );
          })}
          <div className="legend-item legend-off-hours">
            <span className="legend-color off-hours" />
            <span className="legend-label">Off hours</span>
          </div>
        </div>
      )}

      {/* Chart */}
      {enabledRoles.length === 0 ? (
        <div className="graph-status empty">
          Enable at least one role below to see the demand chart.
        </div>
      ) : (
        <div
          ref={chartContainerReference}
          aria-label={`Demand chart for ${DAYS[activeDay]}, use arrow keys to navigate`}
          className={`chart-container${selection ? ' editing' : ''}`}
          role="application"
          tabIndex={0}
          onBlur={handleChartBlur}
          onFocus={handleChartFocus}
          onKeyDown={handleChartKeyDown}
        >
          <ResponsiveContainer height={420} width="100%">
            <BarChart
              barCategoryGap="12%"
              data={chartDataWithHints}
              margin={CHART_MARGIN}
              onClick={handleChartClick}
            >
              <CartesianGrid
                stroke="#e9ecef"
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                axisLine={AXIS_LINE}
                dataKey="hourLabel"
                tick={AXIS_TICK}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                axisLine={AXIS_LINE}
                domain={yDomain}
                label={YAXIS_LABEL_CONFIG}
                tick={AXIS_TICK}
                tickLine={false}
                width={44}
              />
              <Tooltip
                content={renderCustomTooltip}
                cursor={TOOLTIP_CURSOR}
                isAnimationActive={false}
              />

              {/* Hint bar for empty columns — dashed border + "+" */}
              <Bar
                dataKey="_emptyHint"
                fill="transparent"
                isAnimationActive={false}
                maxBarSize={36}
                shape={<EmptyColumnHint />}
                stackId="demand"
              />

              {enabledRoles.map((roleId, roleIndex) => (
                <Bar
                  key={roleId}
                  cursor="pointer"
                  dataKey={`role_${roleId}`}
                  fill={roleColorMap[roleId]}
                  isAnimationActive={false}
                  maxBarSize={36}
                  stackId="demand"
                  onClick={barClickHandlers[roleId]}
                >
                  {chartDataWithHints.map((entry) => {
                    const isSelected =
                      selection &&
                      selection.roleId === roleId &&
                      selection.timeSlot === entry.timeSlot;
                    const isFocused =
                      !selection &&
                      focusedCell &&
                      focusedCell.roleIndex === roleIndex &&
                      focusedCell.hour === entry.hour;
                    const baseColor = roleColorMap[roleId];
                    const fill = entry.isOffHours
                      ? `${baseColor}66`
                      : baseColor;

                    let stroke = 'transparent';
                    let strokeWidth = 0;

                    if (isSelected) {
                      stroke = '#333';
                      strokeWidth = 2;
                    } else if (isFocused) {
                      stroke = '#007bff';
                      strokeWidth = 2;
                    }

                    return (
                      <Cell
                        key={entry.timeSlot}
                        fill={fill}
                        stroke={stroke}
                        strokeWidth={strokeWidth}
                      />
                    );
                  })}
                  {/* Aggregate total label on the top-most role layer */}
                  {roleId === lastRoleId && (
                    <LabelList
                      content={renderTotalLabel}
                      isAnimationActive={false}
                    />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>

          {/* Screen reader announcements */}
          <div aria-live="polite" className="sr-only" role="status">
            {focusedCellDescription}
          </div>

          {/* Click-to-edit popover (portal to body to isolate from extensions) */}
          {selection && (
            <EditPopover
              key={`${selection.roleId}-${selection.day}-${selection.timeSlot}`}
              chartContainerLeft={popoverContainerLeft}
              chartContainerTop={popoverContainerTop}
              chartContainerWidth={popoverContainerWidth}
              dayLabel={DAYS[activeDay]}
              hour={selection.hour}
              initialValue={selection.value}
              roleColor={roleColorMap[selection.roleId]}
              roleName={popoverRoleName}
              x={selection.x}
              y={selection.y}
              onCommit={handleCommit}
              onDiscard={handleDiscard}
            />
          )}

          {/* Role picker for empty cell click */}
          {rolePicker && (
            <div
              ref={rolePickerReference}
              aria-label="Select a role to add demand"
              className="role-picker"
              role="menu"
              style={rolePickerStyle}
              onKeyDown={handleRolePickerKeyDown}
            >
              <div className="role-picker-header">Select role</div>
              {enabledRoles.map((roleId) => {
                const role = roleMap[roleId];

                return (
                  <button
                    key={roleId}
                    className="role-picker-item"
                    role="menuitem"
                    type="button"
                    onClick={() => handleRolePickerSelect(roleId)}
                  >
                    <span
                      className="role-picker-color"
                      style={{ backgroundColor: roleColorMap[roleId] }}
                    />
                    <span className="role-picker-name">
                      {role?.name || `Role ${roleId}`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Undo bar for last committed cell change */}
      {lastCommit && (
        <div className="cell-undo-bar">
          <span className="cell-undo-message">Value updated</span>
          <button
            className="cell-undo-btn"
            type="button"
            onClick={handleUndoLastCommit}
          >
            Undo
          </button>
        </div>
      )}

      {/* Generate Weekly Demand modal */}
      {showGenerateModal && (
        <GenerateWeeklyDemandModal
          locationCategory={locationCategory}
          locationId={locationId}
          onClose={() => setShowGenerateModal(false)}
          onSuccess={onGenerateSuccess}
        />
      )}
    </div>
  );
};

export default React.memo(DemandPlannerGraphComponent);
